import React, { useContext, useMemo, useState } from 'react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';

import { Box, Grid, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import RouteWrapper from './RouteWrapper';
import Loading from '../components/layout/Loading';
import DataTableToolbarWithExportAndSearch from '../components/shared/DataTableToolbarWithExportAndSearch';

import { auth } from '../config/firebase';
import { DataContext } from '../components/data/DataContext';
import { DataContextType } from '../components/data_types/interfaces';
import {
    MIN_RIVAL_ROUNDS,
    Rivalry,
    buildRivalries,
    findFavourite,
    findNemesis,
    findRival,
} from '../components/data/rival_insights';
import { DS, Overline, PageHeader, Panel, enterSx, formatSigned, numericSx, trendColor } from '../components/shared/Dashboard';
import { Avatar, OpponentDialog, RecordBar, RecordText, ResultChips, VersusCard } from '../components/shared/RivalComponents';

/*****************************************************************************
 ** Top-5 list panel
*****************************************************************************/

interface RankedListProps {
    title: string;
    subtitle: string;
    rows: Rivalry[];
    value: (r: Rivalry) => React.ReactNode;
    emptyText: string;
    onOpen: (r: Rivalry) => void;
    delay: number;
}

const RankedList: React.FC<RankedListProps> = ({ title, subtitle, rows, value, emptyText, onOpen, delay }) => (
    <Panel title={title} subtitle={subtitle} grid={{ xs: 12, md: 4 }} delay={delay}>
        {rows.length === 0 ? (
            <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>{emptyText}</Typography>
        ) : rows.map((r, i) => (
            <Box
                key={r.pID}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(r)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpen(r); }}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '18px 32px 1fr auto',
                    alignItems: 'center',
                    gap: 1.25,
                    py: 1,
                    px: 1,
                    mx: -1,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    '&:hover': { background: DS.inset },
                }}
            >
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: DS.textFaint, ...numericSx }}>{i + 1}</Typography>
                <Avatar name={r.name} color={DS.textMuted} size={32} icon={r.icon} iconBorder={r.iconBorder} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}><RecordBar w={r.w} t={r.t} l={r.l} height={4} /></Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>{value(r)}</Box>
            </Box>
        ))}
    </Panel>
);

/*****************************************************************************
 ** All opponents table
*****************************************************************************/

const columns: GridColDef<Rivalry>[] = [
    {
        field: 'name',
        headerName: 'Opponent',
        headerClassName: 'op-header',
        flex: 1,
        minWidth: 160,
        renderCell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar name={row.name} color={DS.textMuted} size={26} icon={row.icon} iconBorder={row.iconBorder} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text }}>{row.name}</Typography>
            </Box>
        ),
    },
    { field: 'played', type: 'number', headerName: 'Rounds', headerClassName: 'op-header', width: 90 },
    {
        field: 'record',
        headerName: 'W–T–L',
        headerClassName: 'op-header',
        width: 130,
        sortable: false,
        valueGetter: (_v, row) => `${row.w}-${row.t}-${row.l}`,
        renderCell: ({ row }) => (
            <Box sx={{ width: '100%' }}>
                <RecordText w={row.w} t={row.t} l={row.l} size={13} />
                <Box sx={{ mt: 0.5 }}><RecordBar w={row.w} t={row.t} l={row.l} height={4} /></Box>
            </Box>
        ),
    },
    { field: 'w', type: 'number', headerName: 'Wins', headerClassName: 'op-header', width: 80 },
    { field: 'winPercent', type: 'number', headerName: 'Win %', headerClassName: 'op-header', width: 85, valueFormatter: (v) => `${v}%` },
    { field: 't', type: 'number', headerName: 'Ties', headerClassName: 'op-header', width: 75 },
    { field: 'tiePercent', type: 'number', headerName: 'Tie %', headerClassName: 'op-header', width: 80, valueFormatter: (v) => `${v}%` },
    { field: 'l', type: 'number', headerName: 'Losses', headerClassName: 'op-header', width: 85 },
    { field: 'lossPercent', type: 'number', headerName: 'Loss %', headerClassName: 'op-header', width: 85, valueFormatter: (v) => `${v}%` },
    {
        field: 'strokeDiff',
        type: 'number',
        headerName: 'Stroke diff',
        headerClassName: 'op-header',
        width: 105,
        renderCell: ({ value }) => (
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: trendColor(value), ...numericSx }}>{formatSigned(value)}</Typography>
        ),
    },
    {
        field: 'strokesPerRound',
        type: 'number',
        headerName: 'Per round',
        headerClassName: 'op-header',
        width: 100,
        renderCell: ({ value }) => (
            <Typography sx={{ fontSize: '13px', color: trendColor(value), ...numericSx }}>{formatSigned(value, 1)}</Typography>
        ),
    },
    {
        field: 'form',
        headerName: 'Last 5',
        headerClassName: 'op-header',
        width: 150,
        sortable: false,
        disableExport: true,
        renderCell: ({ row }) => <ResultChips meetings={row.meetings} />,
    },
    {
        field: 'lastPlayed',
        type: 'number',
        headerName: 'Last played',
        headerClassName: 'op-header',
        width: 130,
        valueFormatter: (v) => (v ? moment(v).format('MMM D, YYYY') : '—'),
    },
];

/*****************************************************************************
 ** Card size tile
*****************************************************************************/

const CardSizeTile: React.FC<{ size: 2 | 3 | 4; data: any; delay: number }> = ({ size, data, delay }) => {
    const m = data.match;
    const rounds = data[`card_${size}_total`] ?? 0;
    return (
        <Box sx={{ p: 2, borderRadius: '12px', background: DS.inset, border: `1px solid ${DS.border}`, opacity: rounds ? 1 : 0.55, ...enterSx(delay) }}>
            <Overline>{size}-player cards</Overline>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
                <Typography sx={{ fontSize: '26px', fontWeight: 800, color: DS.text, lineHeight: 1.2, ...numericSx }}>{rounds}</Typography>
                <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>rounds · {data[`card_${size}_percent`] ?? 0}%</Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', color: DS.textMuted, mb: 1.5, ...numericSx }}>
                {data[`card_${size}_matches_total`] ?? 0} opponents faced
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>Head to head</Typography>
                <RecordText w={m[`card_${size}_wins`] ?? 0} t={m[`card_${size}_draws`] ?? 0} l={m[`card_${size}_losses`] ?? 0} size={13} />
            </Box>
            <RecordBar w={m[`card_${size}_wins`] ?? 0} t={m[`card_${size}_draws`] ?? 0} l={m[`card_${size}_losses`] ?? 0} />
            <Typography sx={{ fontSize: '11px', color: DS.textFaint, mt: 0.75, ...numericSx }}>
                {m[`card_${size}_win_percent`] ?? 0}% wins · {m[`card_${size}_draw_percent`] ?? 0}% ties · {m[`card_${size}_loss_percent`] ?? 0}% losses
            </Typography>
            <Typography sx={{ fontSize: '12px', color: DS.text, mt: 1.25, pt: 1.25, borderTop: `1px solid ${DS.border}`, ...numericSx }}>
                Won the whole card <strong style={{ color: DS.gold }}>{data.card?.[`card_${size}_win_percent`] ?? 0}%</strong> of the time
            </Typography>
        </Box>
    );
};

/*****************************************************************************
 ** Page
*****************************************************************************/

const Opponents = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { matchData, cardSummaryData } = context || {};
    const [selected, setSelected] = useState<Rivalry | null>(null);

    const myPID = auth.currentUser?.uid ?? '';
    const rivalries = useMemo(() => buildRivalries(myPID, matchData ?? []), [myPID, matchData]);

    const nemesis = findNemesis(rivalries);
    const favourite = findFavourite(rivalries);
    const rival = findRival(rivalries, [nemesis?.pID, favourite?.pID]);

    const qualified = rivalries.filter(r => r.played >= MIN_RIVAL_ROUNDS);
    const mostPlayed = orderBy(rivalries, ['played', 'winPercent'], ['desc', 'desc']).slice(0, 5);
    const bestRecords = orderBy(qualified, ['winPercent', 'played'], ['desc', 'desc']).slice(0, 5);
    const worstRecords = orderBy(qualified, ['lossPercent', 'played'], ['desc', 'desc']).slice(0, 5);
    const minText = `Play someone ${MIN_RIVAL_ROUNDS}+ times to rank them.`;

    if (isEmpty(cardSummaryData)) {
        return <Loading />;
    }

    const c = cardSummaryData;

    return (
        <RouteWrapper>
            <PageHeader
                overline="Opponents"
                title="Your rivals"
                subtitle="Everyone you've shared a card with this season. Head to head compares your score with each opponent on the card."
            />

            {/* Overview */}
            <Panel delay={0.05} noPadding>
                <Box sx={{
                    p: { xs: 2, sm: 3 },
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: '1fr 1fr 1.6fr 1fr 1fr' },
                    gap: { xs: 2.5, md: 3 },
                }}>
                    <Box>
                        <Overline>Head to heads</Overline>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: DS.text, ...numericSx }}>{c.total_matches}</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>one per opponent per round</Typography>
                    </Box>
                    <Box>
                        <Overline>Unique opponents</Overline>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: DS.text, ...numericSx }}>{c.total_unique_matches}</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>different players</Typography>
                    </Box>
                    <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                        <Overline>Head to head record</Overline>
                        <RecordText w={c.overall_match_wins} t={c.overall_match_draws} l={c.overall_match_losses} size={26} />
                        <Box sx={{ mt: 0.75 }}>
                            <RecordBar w={c.overall_match_wins} t={c.overall_match_draws} l={c.overall_match_losses} height={8} />
                        </Box>
                    </Box>
                    <Box>
                        <Overline>Win rate</Overline>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: DS.text, ...numericSx }}>{c.overall_match_wins_percent}%</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>
                            {c.overall_match_draws_percent}% ties · {c.overall_match_losses_percent}% losses
                        </Typography>
                    </Box>
                    <Box>
                        <Overline>Avg card size</Overline>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: DS.text, ...numericSx }}>{c.average_card_size}</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>players per round</Typography>
                    </Box>
                </Box>
            </Panel>

            {/* Rivals */}
            <VersusCard
                overline="Nemesis"
                caption="The opponent who beats you most often"
                accent={DS.loss}
                rivalry={nemesis}
                emptyText={`Nobody has a winning record against you yet (min. ${MIN_RIVAL_ROUNDS} rounds).`}
                onOpen={setSelected}
                delay={0.05}
            />
            <VersusCard
                overline="Favourite opponent"
                caption="The opponent you beat most often"
                accent={DS.gain}
                rivalry={favourite}
                emptyText={`No winning record against anyone yet (min. ${MIN_RIVAL_ROUNDS} rounds).`}
                onOpen={setSelected}
                delay={0.1}
            />
            <VersusCard
                overline="Closest rivalry"
                caption="The head to head that could go either way"
                accent={DS.info}
                rivalry={rival}
                emptyText={minText}
                onOpen={setSelected}
                delay={0.15}
            />

            {/* Top lists */}
            <RankedList
                title="Most played"
                subtitle="Who you share a card with most"
                rows={mostPlayed}
                value={r => <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.text, ...numericSx }}>{r.played} <span style={{ color: DS.textMuted, fontWeight: 500 }}>rds</span></Typography>}
                emptyText="You haven't played anyone yet."
                onOpen={setSelected}
                delay={0.1}
            />
            <RankedList
                title="Best records"
                subtitle={`Highest win rate · min. ${MIN_RIVAL_ROUNDS} rounds`}
                rows={bestRecords}
                value={r => <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.gain, ...numericSx }}>{r.winPercent}%</Typography>}
                emptyText={minText}
                onOpen={setSelected}
                delay={0.15}
            />
            <RankedList
                title="Toughest records"
                subtitle={`Highest loss rate · min. ${MIN_RIVAL_ROUNDS} rounds`}
                rows={worstRecords}
                value={r => <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.loss, ...numericSx }}>{r.lossPercent}%</Typography>}
                emptyText={minText}
                onOpen={setSelected}
                delay={0.2}
            />

            {/* By card size */}
            <Panel title="By card size" subtitle="How you do in 2, 3 and 4 player rounds" delay={0.15}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                    {([2, 3, 4] as const).map((size, i) => <CardSizeTile key={size} size={size} data={c} delay={0.15 + i * 0.04} />)}
                </Box>
            </Panel>

            {/* All opponents */}
            <Panel title="All opponents" subtitle="Click an opponent to see every round you've played together" delay={0.2} noPadding>
                <Box sx={{
                    height: 560,
                    width: '100%',
                    borderTop: `1px solid ${DS.border}`,
                    '& .op-header': {
                        color: DS.textMuted,
                        fontSize: '11px !important',
                        fontWeight: '700 !important',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                    },
                    '& .MuiDataGrid-root': { border: 'none', background: 'transparent' },
                    '& .MuiDataGrid-cell': { borderColor: DS.border, display: 'flex', alignItems: 'center', fontSize: '13px', ...numericSx },
                    '& .MuiDataGrid-row': { cursor: 'pointer', '&:hover': { background: `${alpha(DS.brand, 0.06)} !important` } },
                    '& .MuiDataGrid-footerContainer': { borderColor: DS.border },
                    '& .MuiDataGrid-toolbarContainer': { px: 2, pt: 1.5, pb: 1, borderBottom: `1px solid ${DS.border}` },
                    '& .MuiDataGrid-container--top [role="row"]': { background: `${DS.surface} !important` },
                    '& .MuiTablePagination-root': { color: DS.textMuted },
                }}>
                    <DataGrid
                        rows={rivalries}
                        columns={columns}
                        getRowId={(r) => r.pID}
                        density="compact"
                        rowHeight={52}
                        sortingOrder={['desc', 'asc']}
                        slots={{ toolbar: DataTableToolbarWithExportAndSearch }}
                        onRowClick={({ row }) => setSelected(row)}
                        initialState={{
                            columns: { columnVisibilityModel: { w: false, t: false, l: false, tiePercent: false } },
                            pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            sorting: { sortModel: [{ field: 'played', sort: 'desc' }] },
                        }}
                        sx={{ border: 0 }}
                    />
                </Box>
            </Panel>

            <Grid item xs={12} sx={{ mb: 6 }} />

            <OpponentDialog rivalry={selected} onClose={() => setSelected(null)} />
        </RouteWrapper>
    );
};

export default Opponents;
